import React, { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix for default marker icon in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Component to handle map click events
function MapClickHandler({ setCoordinates }) {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      setCoordinates?.(lat, lng);
    },
  });
  return null;
}

// Component to recenter map when coordinates change externally
function MapRecenter({ latitude, longitude }) {
  const map = useMapEvents({});

  useEffect(() => {
    if (latitude && longitude) {
      map.setView([latitude, longitude], map.getZoom());
    }
  }, [latitude, longitude, map]);

  return null;
}

/**
 * MapPointPicker Component
 *
 * A Leaflet-based map component that allows users to pick a point by clicking on the map.
 *
 * @param {number} latitude - Current latitude value
 * @param {number} longitude - Current longitude value
 * @param {function} setCoordinates - Callback function to update coordinates
 * @param {number} [zoom=13] - Initial zoom level (default: 13)
 * @param {number} [height=400] - Map height in pixels (default: 400)
 * @param {string} [width='100%'] - Map width (default: '100%')
 * @param {boolean} [showCoordinates=false] - Whether to display coordinates (default: false)
 */
const MapPointPicker = ({
  latitude,
  longitude,
  setCoordinates,
  zoom = 13,
  height = 400,
  width = "100%",
  showCoordinates = false,
  ...rest
}) => {
  // Default center (Paris, France) if no coordinates provided
  const defaultCenter = [48.8566, 2.3522];
  const center = latitude && longitude ? [latitude, longitude] : defaultCenter;

  // If no coordinates are set initially, set to default center
  const hasSetDefault = useRef(false);
  useEffect(() => {
    if (!hasSetDefault.current && (!latitude || !longitude)) {
      setCoordinates?.(defaultCenter[0], defaultCenter[1]);
      hasSetDefault.current = true;
    }
  }, [latitude, longitude, setCoordinates]);

  return (
    <div style={{ width, height }} {...rest}>
      <MapContainer center={center} zoom={zoom} style={{ width: "100%", height: "100%" }} scrollWheelZoom={true}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapClickHandler setCoordinates={setCoordinates} />

        <MapRecenter latitude={latitude} longitude={longitude} />

        {latitude && longitude && <Marker position={[latitude, longitude]} />}
      </MapContainer>
      {showCoordinates && latitude && longitude && (
        <div style={{ marginTop: "8px" }}>{`Latitude: ${latitude}, Longitude: ${longitude}`}</div>
      )}
    </div>
  );
};

export default MapPointPicker;
